import React from "react";
import { MenuItem, Select } from "@mui/material";
import { AgGridReact } from "@ag-grid-community/react";
import type { ColDef, RowClickedEvent } from "@ag-grid-community/core";
import { useTranslation } from "react-i18next";
import { ModernPagination } from "../../new-features/service-requests/sr-components/modern-pagination";
import { initializeAgGrid } from "../../helpers/ag-grid-init";
import { ENQUIRY_PAGE_SIZE_OPTIONS } from "./enquiry-settings";
import {
    displayCategory,
    displayCurrentRole,
    displayFormType,
    displayText,
    getEnquiryTimestampMs,
    type EnquiryRow,
} from "./enquiry-helpers";
import { EnquiryDateCell, EnquiryFormTypeBadge, EnquiryTruncatedText } from "./enquiry-cell-renderers";
import { EnquiryCopyButton } from "./EnquiryCopyButton";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

initializeAgGrid();

type EnquiryTableProps = {
    data: EnquiryRow[];
    onRowClick: (row: EnquiryRow) => void;
    currentPage: number;
    pageSize: number;
    setPageSize: (size: number) => void;
    setPageNumber: (page: number) => void;
    totalPages: number;
    totalRecords: number;
};

const rowStyle = { cursor: "pointer" };

const enquiryGridColumns: ColDef<EnquiryRow>[] = [
    {
        headerName: "Date",
        colId: "timestamp",
        width: 180,
        sortable: true,
        valueGetter: (params) => getEnquiryTimestampMs(params.data?.timestamp),
        cellRenderer: (params) => <EnquiryDateCell value={params.data?.timestamp} />,
    },
    {
        headerName: "Type",
        colId: "formType",
        width: 120,
        sortable: true,
        valueGetter: (params) => (params.data ? displayFormType(params.data) : "-"),
        cellRenderer: (params) => (
            <EnquiryFormTypeBadge value={params.data ? displayFormType(params.data) : "-"} />
        ),
    },
    {
        headerName: "Category",
        colId: "category",
        width: 140,
        sortable: true,
        valueGetter: (params) => (params.data ? displayCategory(params.data) : "-"),
        cellRenderer: (params) => (
            <EnquiryTruncatedText value={params.data ? displayCategory(params.data) : "-"} />
        ),
    },
    {
        headerName: "Name",
        field: "name",
        width: 160,
        sortable: true,
        cellRenderer: (params) => (
            <span className="font-medium text-gray-900">{displayText(params.value)}</span>
        ),
    },
    {
        headerName: "Phone",
        field: "phoneno",
        width: 130,
        sortable: true,
        cellRenderer: (params) => displayText(params.value),
    },
    {
        headerName: "Email",
        field: "email",
        flex: 1,
        minWidth: 180,
        sortable: true,
        cellRenderer: (params) => <EnquiryTruncatedText value={displayText(params.value)} />,
    },
    {
        headerName: "Message",
        field: "message",
        flex: 1,
        minWidth: 200,
        sortable: true,
        cellRenderer: (params) => <EnquiryTruncatedText value={displayText(params.value)} />,
    },
    {
        headerName: "Role",
        colId: "currentRole",
        width: 150,
        sortable: true,
        valueGetter: (params) => (params.data ? displayCurrentRole(params.data) : "-"),
        cellRenderer: (params) => (params.data ? displayCurrentRole(params.data) : "-"),
    },
    {
        headerName: "",
        colId: "copy",
        width: 56,
        sortable: false,
        suppressMovable: true,
        cellRenderer: (params) => (params.data ? <EnquiryCopyButton row={params.data} /> : null),
    },
];

export const EnquiryTable = ({
    data,
    onRowClick,
    currentPage,
    pageSize,
    setPageSize,
    setPageNumber,
    totalPages,
    totalRecords,
}: EnquiryTableProps) => {
    const { t } = useTranslation("service_request_home");

    return (
        <div>
            <div className="flex flex-col-reverse gap-4 border-b border-gray-100 px-4 py-3 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-gray-600">
                    {t("showing")} <span className="font-semibold text-gray-900">{data?.length || 0}</span>{" "}
                    {t("records_of")} <span className="font-semibold text-gray-900">{totalRecords}</span>{" "}
                    {t("results")}
                </p>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-600">Rows:</span>
                        <Select
                            value={pageSize}
                            onChange={(event) => setPageSize(Number(event.target.value))}
                            size="small"
                            sx={{
                                minWidth: "70px",
                                borderRadius: "8px",
                                backgroundColor: "white",
                                fontWeight: 500,
                                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E7EB" },
                                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#D1D5DB" },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#3A80F4",
                                    borderWidth: "2px",
                                },
                            }}
                        >
                            {ENQUIRY_PAGE_SIZE_OPTIONS.map((size) => (
                                <MenuItem value={size} key={size} sx={{ fontWeight: 500 }}>
                                    {size}
                                </MenuItem>
                            ))}
                        </Select>
                    </div>
                    <ModernPagination
                        pageNumber={currentPage}
                        totalPages={totalPages}
                        handlePageChange={(_event, nextPage) => setPageNumber(nextPage)}
                    />
                </div>
            </div>

            <div className="ag-theme-quartz w-full overflow-hidden" style={{ minHeight: 320 }}>
                <AgGridReact<EnquiryRow>
                    rowData={data}
                    columnDefs={enquiryGridColumns}
                    rowStyle={rowStyle}
                    onRowClicked={(event: RowClickedEvent<EnquiryRow>) => {
                        const target = event.event?.target as HTMLElement | undefined;
                        if (target?.closest("[data-stop-row-click]")) return;
                        if (event.data) onRowClick(event.data);
                    }}
                    suppressRowClickSelection
                    rowHeight={56}
                    headerHeight={48}
                    domLayout="autoHeight"
                    animateRows={false}
                />
            </div>
        </div>
    );
};
